/**
 * Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
 * analytics events keep the historical "owner note" name on purpose — do not rename them.
 *
 * Wording rule (#55/#68, enforced by owners-section-ssr.spec): never the words
 * "verified purchase" — not even negated — anywhere the section renders.
 *
 * One-line explanations for the owner-notes vocabulary (badge, status, pseudonym, own
 * card, disclosure), each distilled from a statement on /how-we-verify-owners/ and
 * deep-linking to its section. Rendered by `OwnerHint` on hover / focus / tap.
 */
import type { OwnerNoteStatus, OwnerPhotoStatus } from 'lib/dealhunt/types'

export const VERIFY_PATH = '/how-we-verify-owners/'

export interface OwnerHintText {
  text: string
  href: string
}

export const PHOTO_HINT: Record<OwnerPhotoStatus, OwnerHintText> = {
  none: {
    text: 'The reader says they own it. We have not seen proof, and we say so — no label here is proof of a purchase.',
    href: `${VERIFY_PATH}#labels`,
  },
  rejected: {
    text: 'The reader says they own it. We have not seen proof, and we say so — no label here is proof of a purchase.',
    href: `${VERIFY_PATH}#labels`,
  },
  pending: {
    text: 'The reader attached a photo of their unit; a moderator has not looked at it yet.',
    href: `${VERIFY_PATH}#labels`,
  },
  verified: {
    text: 'A moderator saw a photo of this reader’s unit and it matched the product. Proof of possession, not purchase.',
    href: `${VERIFY_PATH}#labels`,
  },
}

/**
 * The author's status chip. `held_review` promises a sign-in shortcut only when signing in would
 * publish it (`heldClaimOffered`, #121) — appealed and out-of-resubmits notes wait for a moderator
 * whether or not the author is signed in.
 */
export function statusHint(
  status: OwnerNoteStatus,
  claimed: boolean,
  signInPublishes: boolean = !claimed
): OwnerHintText {
  const href = `${VERIFY_PATH}#checks`
  switch (status) {
    case 'checking':
      return {
        text: 'Automatic checks are running: lived experience with this product, no promotion or links, no personal data, readable.',
        href,
      }
    case 'published':
      return {
        text: 'Live for every reader. It passed the automatic checks; readers can report it and a moderator can hide it at any time.',
        href,
      }
    case 'rejected':
      return {
        text: 'Declined by the automatic checks — the reason is shown here. Edit and resend, or ask for a human look.',
        href,
      }
    case 'held_retry':
      return {
        text: 'We could not run the checks just now; they retry automatically. Only you see it until then.',
        href,
      }
    case 'held_review':
      return !claimed && signInPublishes
        ? {
            text: 'It passed the automatic checks and a moderator is taking a look — sign in and it publishes now.',
            href,
          }
        : { text: 'A moderator reads it before it goes public. Only you see it until then.', href }
    case 'hidden':
      return {
        text: 'Readers reported it; a moderator is reviewing. Only you see it meanwhile.',
        href,
      }
    case 'removed':
      return {
        text: 'A moderator removed it, or a newer review of yours on this product replaced it.',
        href,
      }
    default:
      // A status this build does not know (DealHunt's column is TEXT): say only what is sure.
      return {
        text: 'Readers do not see this review until it is published — you see it because this browser wrote it.',
        href,
      }
  }
}

export const PSEUDONYM_HINT: OwnerHintText = {
  text:
    'Every author writes under a generated pseudonym; a member can choose to show their name ' +
    'and photo beside it. A signed-in author’s email is never shown.',
  href: `${VERIFY_PATH}#pseudonyms`,
}

export const OWN_PSEUDONYM_HINT: OwnerHintText = {
  text: 'Your generated pseudonym. Signing in links your email to it — never shown — and lets you choose a name once.',
  href: `${VERIFY_PATH}#pseudonyms`,
}

export const OWN_CARD_HINT: OwnerHintText = {
  text: 'Your review is public under your pseudonym. This status box, the buttons and your email are not.',
  href: `${VERIFY_PATH}#pseudonyms`,
}

/** The author's readers figure (#138): what a view is, and who else sees it (#155). */
export const OWN_VIEWS_HINT: OwnerHintText = {
  text: 'A reader who scrolled to your review, counted once a day. Signed-in readers see this number too; it never orders the owner reviews.',
  href: `${VERIFY_PATH}#readers`,
}

/** A public card's readers figure, seen by a signed-in reader (#155). */
export const PUBLIC_VIEWS_HINT: OwnerHintText = {
  text: 'Readers who scrolled to this owner review, counted once a day each. It never orders the owner reviews.',
  href: `${VERIFY_PATH}#readers`,
}

/** The own card before (or after) the note is public — held, checking, rejected, removed. */
export const OWN_PRIVATE_LABEL = 'Only you can see this for now'
export const OWN_PRIVATE_HINT: OwnerHintText = {
  text: 'Readers do not see this review until it is published — you see it because this browser wrote it.',
  href: `${VERIFY_PATH}#checks`,
}

/** The author's photo chip (a reader's card uses PHOTO_HINT). */
export const OWN_PHOTO_HINT: Record<Exclude<OwnerPhotoStatus, 'none'>, OwnerHintText> = {
  pending: {
    text: 'A moderator checks that your photo shows this product. When it does, your review gets the Photo-verified label.',
    href: `${VERIFY_PATH}#labels`,
  },
  verified: {
    text: 'A moderator saw your photo and it matched the product. Readers see the Photo-verified label.',
    href: `${VERIFY_PATH}#labels`,
  },
  rejected: {
    text: 'We could not use your photo, so the review shows as Owner-reported. You can attach another one from Edit.',
    href: `${VERIFY_PATH}#labels`,
  },
}

/**
 * Replaces the page-level affiliate line beside the section (#68): the owner-specific part.
 * No link of its own — the section header's "How we verify owners" is right above it
 * (a second one read as a duplicate, 2026-09-11).
 */
export const OWNER_DISCLOSURE =
  'Owner reviews are not paid for, not solicited from specific owners, and not rewritten by us: at most we proofread typos, which is shown on the review’s own page and which the author can undo. The commission SoundGuys may earn on this page comes from retail links, never from owner reviews.'
